package songwritersnotebook.app.crdt

import dev.gitlive.firebase.database.FirebaseDatabase
import dev.gitlive.firebase.database.database
import dev.gitlive.firebase.database.DataSnapshot
import dev.gitlive.firebase.Firebase
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.Clock
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.data.UserPresence
import songwritersnotebook.app.utils.Utils
import songwritersnotebook.app.AppDispatchers

enum class UndoScope { EDITOR, STUDIO }

/**
 * Manages real-time synchronization of Automerge CRDT deltas via Firebase Realtime Database.
 */
class SongCrdtSyncManager(
    private val database: FirebaseDatabase = Firebase.database,
    private var crdtDoc: SongCrdtDoc = SongCrdtDoc()
) {
    private val syncExceptionHandler = CoroutineExceptionHandler { _, throwable ->
        println("DEBUG_CRDT: Uncaught sync error: ${throwable.stackTraceToString()}")
    }
    private val scope = CoroutineScope(SupervisorJob() + AppDispatchers.IO + syncExceptionHandler)
    private var syncJob: Job? = null
    private var lastState: ByteArray? = null
    private val appliedDeltaKeys = mutableSetOf<String>()
    private val crdtMutex = kotlinx.coroutines.sync.Mutex()

    private val _songFlow = MutableStateFlow<Song?>(null)
    val songFlow: StateFlow<Song?> = _songFlow.asStateFlow()

    private val _pendingDeltasCount = MutableStateFlow(0)
    val pendingDeltasCount: StateFlow<Int> = _pendingDeltasCount.asStateFlow()

    private var localPushCount = 0

    private val _presenceFlow = MutableStateFlow<Map<String, UserPresence>>(emptyMap())
    val presenceFlow: StateFlow<Map<String, UserPresence>> = _presenceFlow.asStateFlow()
    private val presenceCache = mutableMapOf<String, MutableMap<String, UserPresence>>()

    // --- Scoped Pro Undo Manager ---
    // PRO SURGICAL UNDO: We store the Song state BEFORE and AFTER a change.
    // This allows a true 3-way merge: [Before] -> [After] vs [Current Live].
    // We surgically reverse only the diff between [Before] and [After].
    data class UndoAction(
        val beforeState: Song,
        val afterState: Song,
        val isForced: Boolean = false
    )

    private val editorUndoStack = mutableListOf<UndoAction>()
    private val editorRedoStack = mutableListOf<UndoAction>()
    private val studioUndoStack = mutableListOf<UndoAction>()
    private val studioRedoStack = mutableListOf<UndoAction>()
    
    // TRACKING: IDs created by this client locally in this session.
    // This allows undo to only remove OUR additions while preserving collaborator work.
    private val locallyAddedSectionIds = mutableSetOf<String>()
    private val locallyAddedLineIds = mutableSetOf<String>()
    private val locallyAddedTrackIds = mutableSetOf<String>()
    private val locallyAddedTrackItemIds = mutableSetOf<String>()
    
    private val MAX_STACK_SIZE = 50
    private var isApplyingUndoRedo = false
    private var lastEditorUndoPushTime = 0L
    private var lastStudioUndoPushTime = 0L
    private val UNDO_DEBOUNCE_MS = 1500L

    private val _canEditorUndo = MutableStateFlow(false)
    val canEditorUndo: StateFlow<Boolean> = _canEditorUndo.asStateFlow()

    private val _canEditorRedo = MutableStateFlow(false)
    val canEditorRedo: StateFlow<Boolean> = _canEditorRedo.asStateFlow()

    private val _canStudioUndo = MutableStateFlow(false)
    val canStudioUndo: StateFlow<Boolean> = _canStudioUndo.asStateFlow()

    private val _canStudioRedo = MutableStateFlow(false)
    val canStudioRedo: StateFlow<Boolean> = _canStudioRedo.asStateFlow()

    private var isCatchingUp = true
    private val preferencesManager = songwritersnotebook.app.utils.PreferencesManager()
    private val deviceId = preferencesManager.deviceId
    val sessionDeviceId = com.benasher44.uuid.uuid4().toString()

    private var currentSongId: String? = null
    val activeSongId: String? get() = currentSongId
    private var lastLoadedSnapshot: ByteArray? = null
    
    private var currentOwnerId: String = ""
    private var currentCollaborators: List<String> = emptyList()
    private var currentViewers: List<String> = emptyList()

    private var uiUpdateJob: Job? = null
    private val UI_BATCH_WINDOW_MS = 30L
    private val CATCHUP_QUIET_PERIOD_MS = 250L

    /**
     * PRO UI BATCHER: Schedules a UI update after a small delay.
     * If multiple updates are requested within the window, they are batched.
     * During catch-up, it waits for a longer quiet period.
     */
    private fun requestUiUpdate(immediate: Boolean = false) {
        if (immediate) {
            uiUpdateJob?.cancel()
            _songFlow.value = getReconstructedSong()
            return
        }

        uiUpdateJob?.cancel()
        uiUpdateJob = scope.launch {
            val delayMs = if (isCatchingUp) CATCHUP_QUIET_PERIOD_MS else UI_BATCH_WINDOW_MS
            delay(delayMs)
            
            crdtMutex.withLock {
                _songFlow.value = getReconstructedSong()
                if (isCatchingUp) {
                    println("DEBUG_CRDT: Catch-up complete. UI Updated.")
                    isCatchingUp = false
                }
            }
        }
    }

    private fun pushToUndoStack(before: Song, after: Song, scope: UndoScope, force: Boolean = false) {
        if (isApplyingUndoRedo) return
        
        val now = Clock.System.now().toEpochMilliseconds()
        
        val (undoStack, lastPushTime) = when (scope) {
            UndoScope.EDITOR -> editorUndoStack to lastEditorUndoPushTime
            UndoScope.STUDIO -> studioUndoStack to lastStudioUndoPushTime
        }

        // ELITE LOGIC: Merge sequential changes within the debounce window
        if (!force && undoStack.isNotEmpty() && !undoStack.last().isForced && (now - lastPushTime < UNDO_DEBOUNCE_MS)) {
            val lastAction = undoStack.removeAt(undoStack.size - 1)
            // Combined Action: From the VERY first "Before" to the latest "After"
            undoStack.add(UndoAction(lastAction.beforeState, after, isForced = false))
            return
        }

        undoStack.add(UndoAction(before, after, isForced = force))
        if (undoStack.size > MAX_STACK_SIZE) undoStack.removeAt(0)
        
        when (scope) {
            UndoScope.EDITOR -> {
                editorRedoStack.clear()
                lastEditorUndoPushTime = now
            }
            UndoScope.STUDIO -> {
                studioRedoStack.clear()
                lastStudioUndoPushTime = now
            }
        }
        
        updateUndoRedoStatus()
    }

    private fun updateUndoRedoStatus() {
        _canEditorUndo.value = editorUndoStack.isNotEmpty()
        _canEditorRedo.value = editorRedoStack.isNotEmpty()
        _canStudioUndo.value = studioUndoStack.isNotEmpty()
        _canStudioRedo.value = studioRedoStack.isNotEmpty()
    }

    private fun hasRemotePresence(): Boolean {
        val currentDeviceId = deviceId
        return _presenceFlow.value.values.any { it.deviceId != currentDeviceId }
    }

    private fun pushCatchUpDelta() {
        scope.launch {
            crdtMutex.withLock {
                // PRO SYNC FIX: A catch-up delta must contain the ENTIRE document state
                // because new collaborators might have missed the initial deltas due to limitToLast(10).
                val fullState = crdtDoc.save()
                if (fullState.isNotEmpty()) {
                    val deltaBase64 = Utils.byteArrayToBase64(fullState)
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val userId = "anonymous" // System-level catchup
                    val key = "${timestamp}_catchup"
                    
                    // Do NOT update lastState here, lastState is only for incremental keystrokes.
                    appliedDeltaKeys.add(key)
                    
                    _pendingDeltasCount.value++
                    localPushCount++
                    
                    try {
                        val deltasRef = database.reference("songs/$currentSongId/deltas")
                        deltasRef.child(key).setValue(deltaBase64)
                        println("DEBUG_CRDT: Pushed full-state catch-up delta $key.")
                    } catch (e: Exception) {
                        println("DEBUG_CRDT_ERROR: Error pushing catch-up delta: $e")
                    } finally {
                        _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                    }
                }
            }
        }
    }

    /**
     * ELITE 3-WAY TEXT MERGE:
     * We calculate what changed between [before] and [after] (Our Action).
     * Then we apply the INVERSE of that change to the [current] state.
     * This preserves any characters that were inserted by collaborators in [current].
     */
    private fun mergeTextSurgically(before: String, after: String, current: String): String {
        if (before == after) return current
        if (after == current) return before

        // 1. Identify what we changed: Diff(before -> after)
        // Simple prefix/suffix diffing for local actions
        var prefixLen = 0
        while (prefixLen < before.length && prefixLen < after.length && before[prefixLen] == after[prefixLen]) prefixLen++
        
        var suffixLen = 0
        while (suffixLen < (before.length - prefixLen) && 
               suffixLen < (after.length - prefixLen) && 
               before[before.length - 1 - suffixLen] == after[after.length - 1 - suffixLen]) suffixLen++
        
        // 2. Identify the INVERSE change: Diff(after -> before)
        val inverseInsertText = before.substring(prefixLen, before.length - suffixLen)

        // 3. Apply the INVERSE to 'current'
        // We find our change's anchor points in the 'current' string.
        val currentPrefix = current.substring(0, prefixLen.coerceAtMost(current.length))
        val currentSuffix = current.substring((current.length - suffixLen).coerceAtLeast(0))
        
        // Reconstruct: [Everything before our change] + [Our Reverted Content] + [Everything after our change]
        return currentPrefix + inverseInsertText + currentSuffix
    }

    suspend fun undo(currentSong: Song, userId: String, scope: UndoScope) {
        val (undoStack, redoStack) = when (scope) {
            UndoScope.EDITOR -> editorUndoStack to editorRedoStack
            UndoScope.STUDIO -> studioUndoStack to studioRedoStack
        }
        if (undoStack.isEmpty()) return

        crdtMutex.withLock {
            isApplyingUndoRedo = true
            
            // 1. Get the action to reverse
            val action = undoStack.removeAt(undoStack.size - 1)
            val before = action.beforeState
            val after = action.afterState
            
            // 2. Save the original action for Redo
            redoStack.add(action)
            
            // 3. SURGICAL 3-WAY MERGE: 
            // We want to revert OUR changes but keep COLLABORATOR changes.
            val mergedSong = when (scope) {
                UndoScope.EDITOR -> {
                    // Process existing sections
                    val finalSections = currentSong.sections.mapNotNull { currentSection ->
                        val beforeSection = before.sections.find { it.id == currentSection.id }
                        val afterSection = after.sections.find { it.id == currentSection.id }
                        
                        if (beforeSection == null && afterSection != null) {
                            // Local Addition: We added this section in this action.
                            // UNDO: Remove it (return null) if it was OUR addition.
                            if (locallyAddedSectionIds.contains(currentSection.id)) null else currentSection
                        } else if (beforeSection != null && afterSection == null) {
                            // Local Deletion: We deleted this section in this action.
                            // UNDO: Restore it (return beforeSection).
                            beforeSection
                        } else if (beforeSection != null && afterSection != null) {
                            // Section existed throughout. Merge contents.
                            val mergedLines = currentSection.lines.mapNotNull { currentLine ->
                                val bLine = beforeSection.lines.find { it.id == currentLine.id }
                                val aLine = afterSection.lines.find { it.id == currentLine.id }
                                
                                if (bLine == null && aLine != null) {
                                    // Local Line Addition: Undo -> Remove if ours
                                    if (locallyAddedLineIds.contains(currentLine.id)) null else currentLine
                                } else if (bLine != null && aLine == null) {
                                    // Local Line Deletion: Undo -> Restore
                                    bLine
                                } else if (bLine != null && aLine != null) {
                                    // Line exists in all states. Character merge!
                                    val tabsToRemove = aLine.tabs.filter { aTab -> !bLine.tabs.any { it.id == aTab.id } }
                                    val tabsToAdd = bLine.tabs.filter { bTab -> !aLine.tabs.any { it.id == bTab.id } }
                                    val mergedTabs = currentLine.tabs.filter { cTab -> !tabsToRemove.any { it.id == cTab.id } } + tabsToAdd
                                    
                                    val newText = mergeTextSurgically(bLine.text, aLine.text, currentLine.text)
                                    if (newText != currentLine.text) {
                                        // Update CRDT atomically for this line
                                        val diff = diffStrings(currentLine.text, newText)
                                        crdtDoc.spliceLineText(currentSection.id, currentLine.id, diff.index, diff.deleteCount, diff.insertText)
                                    }

                                    currentLine.copy(
                                        text = newText,
                                        tabs = mergedTabs
                                    )
                                } else {
                                    currentLine
                                }
                            }.toMutableList()

                            // Restore lines we deleted that might be gone from current
                            beforeSection.lines.forEach { bLine ->
                                if (!afterSection.lines.any { it.id == bLine.id } && !mergedLines.any { it.id == bLine.id }) {
                                    mergedLines.add(bLine)
                                }
                            }

                            val tabsToRemove = afterSection.tabs.filter { aTab -> !beforeSection.tabs.any { it.id == aTab.id } }
                            val tabsToAdd = beforeSection.tabs.filter { bTab -> !afterSection.tabs.any { it.id == bTab.id } }
                            val mergedTabs = currentSection.tabs.filter { cTab -> !tabsToRemove.any { it.id == cTab.id } } + tabsToAdd

                            val newTitle = mergeTextSurgically(beforeSection.title, afterSection.title, currentSection.title)
                            if (newTitle != currentSection.title) {
                                val diff = diffStrings(currentSection.title, newTitle)
                                crdtDoc.spliceSectionTitle(currentSection.id, diff.index, diff.deleteCount, diff.insertText)
                            }

                            currentSection.copy(
                                title = newTitle,
                                lines = mergedLines.sortedBy { it.position },
                                tabs = mergedTabs
                            )
                        } else {
                            // Collaborator Addition: keep it.
                            currentSection
                        }
                    }.toMutableList()

                    // B. Restore sections we deleted that are gone from the world
                    before.sections.forEach { bSection ->
                        if (!after.sections.any { it.id == bSection.id } && !finalSections.any { it.id == bSection.id }) {
                            finalSections.add(bSection)
                        }
                    }

                    val newSongTitle = mergeTextSurgically(before.title, after.title, currentSong.title)
                    if (newSongTitle != currentSong.title) {
                        val diff = diffStrings(currentSong.title, newSongTitle)
                        crdtDoc.spliceSongTitle(diff.index, diff.deleteCount, diff.insertText)
                    }

                    val newSongNotes = mergeTextSurgically(before.notes, after.notes, currentSong.notes)
                    if (newSongNotes != currentSong.notes) {
                        val diff = diffStrings(currentSong.notes, newSongNotes)
                        crdtDoc.spliceSongNotes(diff.index, diff.deleteCount, diff.insertText)
                    }

                    currentSong.copy(
                        sections = finalSections.sortedBy { it.position },
                        title = newSongTitle,
                        notes = newSongNotes,
                        guitarCapo = if (before.guitarCapo != after.guitarCapo) before.guitarCapo else currentSong.guitarCapo,
                        guitarTuning = if (before.guitarTuning != after.guitarTuning) before.guitarTuning else currentSong.guitarTuning,
                        lastModified = Clock.System.now()
                    )
                }
                UndoScope.STUDIO -> {
                    // Similar Surgical Merge for Studio State
                    currentSong.copy(
                        studioState = before.studioState, // Structural undo for tracks is complex, fallback to snapshot for now
                        chordDefinitions = before.chordDefinitions,
                        bpm = before.bpm,
                        lastModified = Clock.System.now()
                    )
                }
            }

            // 4. Update CRDT with non-text fields (structural)
            crdtDoc.update(mergedSong)
            val delta = crdtDoc.getChangesSince(lastState)
            
            if (delta.isNotEmpty()) {
                val timestamp = Clock.System.now().toEpochMilliseconds()
                val key = "${timestamp}_$userId"
                appliedDeltaKeys.add(key)
                
                @OptIn(kotlin.io.encoding.ExperimentalEncodingApi::class)
                val deltaBase64 = kotlin.io.encoding.Base64.encode(delta)
                currentSongId?.let { sId ->
                    _pendingDeltasCount.value++
                    this@SongCrdtSyncManager.scope.launch {
                        try {
                            database.reference("songs/$sId/deltas/$key").setValue(deltaBase64)
                        } catch (e: Exception) {
                            println("DEBUG_CRDT: Error pushing undo delta: $e")
                        } finally {
                            _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                        }
                    }
                }
                lastState = crdtDoc.save()
            }
            
            requestUiUpdate(immediate = true)
            isApplyingUndoRedo = false
            updateUndoRedoStatus()
        }
    }

    suspend fun redo(currentSong: Song, userId: String, scope: UndoScope) {
        val (undoStack, redoStack) = when (scope) {
            UndoScope.EDITOR -> editorUndoStack to editorRedoStack
            UndoScope.STUDIO -> studioUndoStack to studioRedoStack
        }
        if (redoStack.isEmpty()) return

        crdtMutex.withLock {
            isApplyingUndoRedo = true
            
            // 1. Get the action to re-apply
            val action = redoStack.removeAt(redoStack.size - 1)
            val before = action.beforeState
            val after = action.afterState
            
            // 2. Save the original action for Undo
            undoStack.add(action)
            
            // 3. SURGICAL REDO: Redo is authoritative for our intent, but we still 
            // merge to preserve collaborator work added in the interim.
            val mergedSong = when (scope) {
                UndoScope.EDITOR -> {
                    val finalSections = currentSong.sections.mapNotNull { currentSection ->
                        val bSection = before.sections.find { it.id == currentSection.id }
                        val aSection = after.sections.find { it.id == currentSection.id }
                        
                        if (bSection != null && aSection == null) {
                            // Redo Deletion: This section was in the world, then we deleted it.
                            null
                        } else if (bSection == null && aSection != null) {
                            // Redo Addition: We added this section in this action.
                            aSection
                        } else if (bSection != null && aSection != null) {
                            // Section existed throughout. Merge contents.
                            val mergedLines = currentSection.lines.mapNotNull { currentLine ->
                                val bLine = bSection.lines.find { it.id == currentLine.id }
                                val aLine = aSection.lines.find { it.id == currentLine.id }
                                
                                if (bLine != null && aLine == null) {
                                    // Local Line Deletion: Redo -> Remove
                                    null
                                } else if (bLine == null && aLine != null) {
                                    // Local Line Addition: Redo -> Restore
                                    aLine
                                } else if (bLine != null && aLine != null) {
                                    // Line exists in all states. Character merge! (Note swapped before/after for redo)
                                    val tabsToRemove = bLine.tabs.filter { bTab -> !aLine.tabs.any { it.id == bTab.id } }
                                    val tabsToAdd = aLine.tabs.filter { aTab -> !bLine.tabs.any { it.id == aTab.id } }
                                    val mergedTabs = currentLine.tabs.filter { cTab -> !tabsToRemove.any { it.id == cTab.id } } + tabsToAdd
                                    
                                    val newText = mergeTextSurgically(aLine.text, bLine.text, currentLine.text)
                                    if (newText != currentLine.text) {
                                        val diff = diffStrings(currentLine.text, newText)
                                        crdtDoc.spliceLineText(currentSection.id, currentLine.id, diff.index, diff.deleteCount, diff.insertText)
                                    }

                                    currentLine.copy(
                                        text = newText,
                                        tabs = mergedTabs
                                    )
                                } else {
                                    currentLine
                                }
                            }.toMutableList()

                            // Restore lines we added that might be gone from current
                            aSection.lines.forEach { aLine ->
                                if (!bSection.lines.any { it.id == aLine.id } && !mergedLines.any { it.id == aLine.id }) {
                                    mergedLines.add(aLine)
                                }
                            }

                            val tabsToRemove = bSection.tabs.filter { bTab -> !aSection.tabs.any { it.id == bTab.id } }
                            val tabsToAdd = aSection.tabs.filter { aTab -> !bSection.tabs.any { it.id == aTab.id } }
                            val mergedTabs = currentSection.tabs.filter { cTab -> !tabsToRemove.any { it.id == cTab.id } } + tabsToAdd

                            val newTitle = mergeTextSurgically(aSection.title, bSection.title, currentSection.title)
                            if (newTitle != currentSection.title) {
                                val diff = diffStrings(currentSection.title, newTitle)
                                crdtDoc.spliceSectionTitle(currentSection.id, diff.index, diff.deleteCount, diff.insertText)
                            }

                            currentSection.copy(
                                title = newTitle,
                                lines = mergedLines.sortedBy { it.position },
                                tabs = mergedTabs
                            )
                        } else {
                            currentSection
                        }
                    }.toMutableList()

                    after.sections.forEach { aSection ->
                        if (!before.sections.any { it.id == aSection.id } && !finalSections.any { it.id == aSection.id }) {
                            finalSections.add(aSection)
                        }
                    }

                    val newSongTitle = mergeTextSurgically(after.title, before.title, currentSong.title)
                    if (newSongTitle != currentSong.title) {
                        val diff = diffStrings(currentSong.title, newSongTitle)
                        crdtDoc.spliceSongTitle(diff.index, diff.deleteCount, diff.insertText)
                    }

                    val newSongNotes = mergeTextSurgically(after.notes, before.notes, currentSong.notes)
                    if (newSongNotes != currentSong.notes) {
                        val diff = diffStrings(currentSong.notes, newSongNotes)
                        crdtDoc.spliceSongNotes(diff.index, diff.deleteCount, diff.insertText)
                    }

                    currentSong.copy(
                        sections = finalSections.sortedBy { it.position },
                        title = newSongTitle,
                        notes = newSongNotes,
                        guitarCapo = if (before.guitarCapo != after.guitarCapo) after.guitarCapo else currentSong.guitarCapo,
                        guitarTuning = if (before.guitarTuning != after.guitarTuning) after.guitarTuning else currentSong.guitarTuning,
                        lastModified = Clock.System.now()
                    )
                }
                UndoScope.STUDIO -> {
                    currentSong.copy(
                        studioState = after.studioState,
                        chordDefinitions = after.chordDefinitions,
                        bpm = after.bpm,
                        lastModified = Clock.System.now()
                    )
                }
            }

            crdtDoc.update(mergedSong)
            val delta = crdtDoc.getChangesSince(lastState)
            
            if (delta.isNotEmpty()) {
                val timestamp = Clock.System.now().toEpochMilliseconds()
                val key = "${timestamp}_$userId"
                appliedDeltaKeys.add(key)
                
                @OptIn(kotlin.io.encoding.ExperimentalEncodingApi::class)
                val deltaBase64 = kotlin.io.encoding.Base64.encode(delta)
                currentSongId?.let { sId ->
                    _pendingDeltasCount.value++
                    this@SongCrdtSyncManager.scope.launch {
                        try {
                            database.reference("songs/$sId/deltas/$key").setValue(deltaBase64)
                        } catch (e: Exception) {
                            println("DEBUG_CRDT: Error pushing redo delta: $e")
                        } finally {
                            _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                        }
                    }
                }
                lastState = crdtDoc.save()
            }
            
            requestUiUpdate(immediate = true)
            isApplyingUndoRedo = false
            updateUndoRedoStatus()
        }
    }

    private data class StringDiff(val index: Int, val deleteCount: Int, val insertText: String)
    private fun diffStrings(oldText: String, newText: String): StringDiff {
        var prefixLen = 0
        while (prefixLen < oldText.length && prefixLen < newText.length && oldText[prefixLen] == newText[prefixLen]) {
            prefixLen++
        }

        var suffixLen = 0
        while (suffixLen < oldText.length - prefixLen && 
               suffixLen < newText.length - prefixLen && 
               oldText[oldText.length - 1 - suffixLen] == newText[newText.length - 1 - suffixLen]) {
            suffixLen++
        }

        val deleteCount = oldText.length - prefixLen - suffixLen
        val insertText = newText.substring(prefixLen, newText.length - suffixLen)
        return StringDiff(prefixLen, deleteCount, insertText)
    }

    fun startSync(songId: String, initialData: ByteArray?, initialSong: Song? = null, forceReboot: Boolean = false) {
        scope.launch {
            crdtMutex.withLock {
                val isDifferentSong = currentSongId != songId
                
                if (isDifferentSong) {
                    currentOwnerId = ""
                    currentCollaborators = emptyList()
                    currentViewers = emptyList()
                }

                var metadataChanged = false
                initialSong?.let {
                    val newOwner = it.ownerId.ifBlank { currentOwnerId }
                    val newCollabs = it.collaborators
                    val newViewers = it.viewers
                    
                    if (newOwner != currentOwnerId || newCollabs != currentCollaborators || newViewers != currentViewers) {
                        currentOwnerId = newOwner
                        currentCollaborators = newCollabs
                        currentViewers = newViewers
                        metadataChanged = true
                    }
                }

                // Idempotency check inside the lock
                if (!forceReboot && !isDifferentSong && initialData != null && lastLoadedSnapshot?.contentEquals(initialData) == true) {
                    if (metadataChanged && !isDifferentSong && crdtDoc.getSong().sections.isNotEmpty()) {
                        _songFlow.value = getReconstructedSong()
                    }
                    return@withLock 
                }

                lastLoadedSnapshot = initialData

                if (isDifferentSong || forceReboot) {
                    println("DEBUG_CRDT: Starting sync for NEW song $songId (forceReboot=$forceReboot)")
                    syncJob?.cancel()
                    _songFlow.value = null
                    _presenceFlow.value = emptyMap()
                    presenceCache.clear()
                    
                    editorUndoStack.clear()
                    editorRedoStack.clear()
                    studioUndoStack.clear()
                    studioRedoStack.clear()
                    
                    locallyAddedSectionIds.clear()
                    locallyAddedLineIds.clear()
                    locallyAddedTrackIds.clear()
                    locallyAddedTrackItemIds.add(com.benasher44.uuid.uuid4().toString()) // Placeholder to avoid empty set issues
                    locallyAddedTrackItemIds.clear()
                    
                    updateUndoRedoStatus()

                    crdtDoc = SongCrdtDoc()
                    lastState = null
                    appliedDeltaKeys.clear() // ONLY clear for a different song or hard reboot
                    currentSongId = songId
                    
                    // Reset metadata if it's a completely different song and no initialSong provided
                    if (isDifferentSong && initialSong == null) {
                        currentOwnerId = ""
                        currentCollaborators = emptyList()
                        currentViewers = emptyList()
                    }
                    
                    isCatchingUp = true
                } else {
                    println("DEBUG_CRDT: Merging update for existing song $songId. Preserving ${appliedDeltaKeys.size} applied deltas.")
                }

                if (initialData != null) {
                    val beforeHeads = crdtDoc.getHeadsHashes()
                    var crdtLoadFailed = false
                    try {
                        if (isDifferentSong || forceReboot) {
                            crdtDoc.load(initialData)
                            lastState = initialData
                        } else {
                            // STATE ISOLATION: Merge the cloud data into memory.
                            // We DO NOT update lastState here. This ensures that the next
                            // local edit is still calculated against the last version WE saved,
                            // preserving our typing even if the cloud is slightly behind or ahead.
                            crdtDoc.merge(initialData)
                        }
                    } catch (e: Throwable) {
                        println("CRDT: Failed to load/merge binary state: ${e.message}. Falling back to vault data.")
                        crdtLoadFailed = true
                    }
                    val afterHeads = crdtDoc.getHeadsHashes()
                    
                    if (!crdtLoadFailed) {
                        val reconstructed = getReconstructedSong()
                        val hasCrdtContent = reconstructed.sections.any { s -> s.lines.any { it.text.isNotBlank() } } || 
                                             (reconstructed.studioState?.tracks?.any { it.items.isNotEmpty() } == true)
                        val hasVaultContent = initialSong != null && (initialSong.sections.any { s -> s.lines.any { it.text.isNotBlank() } } || 
                                             (initialSong.studioState?.tracks?.any { it.items.isNotEmpty() } == true))
                        
                        if (!hasCrdtContent && hasVaultContent) {
                            crdtDoc.update(initialSong!!)
                            lastState = crdtDoc.save()
                        }
                        
                        if (isDifferentSong || forceReboot) {
                            requestUiUpdate(immediate = true)
                        } else if (isCatchingUp) {
                            // We must call requestUiUpdate to emit the fully merged song if we were catching up,
                            // otherwise the UI will be stuck with the empty shell it started with.
                            requestUiUpdate(immediate = false)
                        }
                    } else if (initialSong != null) {
                        val hasLyrics = initialSong.sections.any { it.lines.isNotEmpty() || it.title.isNotEmpty() }
                        val hasStudio = initialSong.studioState?.tracks?.any { it.items.isNotEmpty() } == true
                        
                        if (hasLyrics || hasStudio) {
                            crdtDoc.update(initialSong)
                            lastState = crdtDoc.save()
                            requestUiUpdate(immediate = true)
                        } else {
                            requestUiUpdate(immediate = true)
                        }
                    } else {
                        requestUiUpdate(immediate = true)
                    }
                } else if (initialSong != null && (isDifferentSong || forceReboot)) {
                    val hasLyrics = initialSong.sections.any { it.lines.isNotEmpty() || it.title.isNotEmpty() }
                    val hasStudio = initialSong.studioState?.tracks?.any { it.items.isNotEmpty() } == true
                    
                    if (hasLyrics || hasStudio) {
                        crdtDoc.update(initialSong)
                        lastState = crdtDoc.save()
                        requestUiUpdate(immediate = true)
                    }
                }

                // Launch Sync Job if needed
                if (syncJob == null || !syncJob!!.isActive) {
                    syncJob = launchSyncJob(songId, initialSong)
                }
            }
        }
    }

    private fun CoroutineScope.launchSyncJob(songId: String, initialSong: Song?): Job {
        return launch {
            val songRef = database.reference("songs/$songId")

            // CRITICAL PERMISSION BOOTSTRAP: For older songs that lack an 'access' node,
            // the owner must write their ID to RTDB to unlock the rules for themselves
            // and subsequently for collaborators. We strictly only allow the true owner to do this.
            initialSong?.let {
                val auth = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                if (auth != null && auth.uid == it.ownerId) {
                    try {
                        songRef.child("ownerId").setValue(it.ownerId)
                        
                        // Also explicitly bootstrap the 'access' node so Android collaborators
                        // don't get 'Permission Denied' errors if the backend hasn't triggered yet.
                        val accessMap = mutableMapOf<String, String>()
                        accessMap[it.ownerId] = "owner"
                        it.collaborators.forEach { uid -> accessMap[uid] = "collaborator" }
                        it.viewers.forEach { uid -> accessMap[uid] = "viewer" }
                        songRef.child("access").setValue(accessMap)
                    } catch (e: Exception) {
                        println("DEBUG_REPO: Error setting ownerId or access: $e")
                    }
                }
            }

            // --- DELTA LISTENER (Song Updates) ---
            launch {
                var retryDelay = 2000L
                while (isActive) {
                    try {
                        songRef.child("deltas").limitToLast(10).childEvents()
                            .catch { e ->
                                if (e.message?.contains("permission", ignoreCase = true) == true) {
                                    println("DEBUG_CRDT: Permission denied for deltas on $songId. Retrying in ${retryDelay}ms...")
                                    throw e // Trigger retry loop
                                } else {
                                    println("DEBUG_CRDT_DIAG: Deltas flow caught exception: $e")
                                    throw e
                                }
                            }
                            .collect { event ->
                                val snapshot = event.snapshot
                                println("DEBUG_CRDT_DIAG: deltas childEvent fired. key=${snapshot.key}, exists=${snapshot.exists}")
                                retryDelay = 2000L // Reset delay on success
                                val key = snapshot.key
                                if (key != null && snapshot.exists) {
                                    val deltaBase64 = try {
                                        snapshot.value as? String ?: snapshot.value<String>()
                                    } catch (e: Exception) {
                                        println("DEBUG_CRDT_PARSE: Error parsing delta $key: $e")
                                        null
                                    }
                                    if (deltaBase64 != null) {
                                        crdtMutex.withLock {
                                            if (key !in appliedDeltaKeys) {
                                                try {
                                                    val delta = Utils.base64ToByteArray(deltaBase64)
                                                    crdtDoc.merge(delta)
                                                    appliedDeltaKeys.add(key)
                                                    lastState = crdtDoc.save()
                                                    requestUiUpdate()
                                                } catch (e: Exception) {
                                                    println("DEBUG_CRDT: Error applying delta $key: $e")
                                                }
                                            }
                                        }
                                    }
                                }
                                isCatchingUp = false // We just rely on debounce
                            }
                    } catch (e: Exception) {
                        println("DEBUG_CRDT_DIAG: Outer deltas flow caught exception: $e")
                        delay(retryDelay)
                        retryDelay = (retryDelay * 2).coerceAtMost(30000L)
                    }
                }
            }

            // --- PRESENCE LISTENER (Collaborators) ---
            launch {
                var retryDelay = 2000L
                while (isActive) {
                    try {
                        songRef.child("presence").childEvents()
                            .catch { e ->
                                if (e.message?.contains("permission", ignoreCase = true) == true) {
                                    throw e // Trigger retry loop
                                } else {
                                    println("DEBUG_CRDT_DIAG: Presence flow caught exception: $e")
                                    throw e
                                }
                            }
                            .collect { event ->
                                println("DEBUG_CRDT_DIAG: presence childEvent fired. type=${event.type}, key=${event.snapshot.key}")
                                retryDelay = 2000L // Reset
                                
                                val type = event.type
                                val userId = event.snapshot.key ?: return@collect
                                
                                if (type == dev.gitlive.firebase.database.ChildEvent.Type.REMOVED || !event.snapshot.exists) {
                                    presenceCache.remove(userId)
                                } else {
                                    val userDevices = mutableMapOf<String, UserPresence>()
                                    for (deviceSnapshot in event.snapshot.children) {
                                        if (deviceSnapshot.key != "lastActive") {
                                            try {
                                                val p = parseUserPresenceFromSnapshot(deviceSnapshot)
                                                val now = Clock.System.now().toEpochMilliseconds()
                                                if (now - p.lastActive < 120_000) {
                                                    userDevices[p.deviceId] = p
                                                }
                                            } catch (e: Exception) {
                                                println("DEBUG_CRDT_PARSE: Error ignoring presence node: $e")
                                            }
                                        }
                                    }
                                    presenceCache[userId] = userDevices
                                }
                                
                                val result = mutableMapOf<String, UserPresence>()
                                for (devices in presenceCache.values) {
                                    result.putAll(devices)
                                }
                                
                                val wasAlone = !hasRemotePresence()
                                _presenceFlow.value = result
                                val isCollaborating = hasRemotePresence()
                                
                                if (wasAlone && isCollaborating) {
                                    pushCatchUpDelta()
                                }
                            }
                    } catch (e: Exception) {
                        println("DEBUG_CRDT_DIAG: Outer presence flow caught exception: $e")
                        delay(retryDelay)
                        retryDelay = (retryDelay * 2).coerceAtMost(30000L)
                    }
                }
            }

            // --- PRESENCE CLEANUP LOOP (Local Pruning) ---
            launch {
                while (isActive) {
                    delay(15000) // Run every 15 seconds
                    val current = _presenceFlow.value
                    if (current.isNotEmpty()) {
                        val now = Clock.System.now().toEpochMilliseconds()
                        val filtered = current.filter { (_, p) ->
                            now - p.lastActive < 60000 // Prune if older than 60s
                        }
                        if (filtered.size != current.size) {
                            println("DEBUG_CRDT: Pruned ${current.size - filtered.size} stale presences locally.")
                            _presenceFlow.value = filtered
                            
                            val activeKeys = filtered.keys
                            presenceCache.values.forEach { userDevices ->
                                userDevices.keys.retainAll(activeKeys)
                            }
                        }
                    }
                }
            }
        }
    }

    private fun parseUserPresenceFromSnapshot(snapshot: dev.gitlive.firebase.database.DataSnapshot): UserPresence {
        fun <T> safeRead(key: String, reader: () -> T): T? {
            return if (snapshot.child(key).exists) {
                try { reader() } catch (e: Exception) { 
                    println("DEBUG_CRDT_PARSE: Error parsing presence $key: $e")
                    null 
                }
            } else null
        }

        fun safeReadDouble(key: String): Double {
            if (!snapshot.child(key).exists) return 0.0
            return try {
                snapshot.child(key).value<Double>()
            } catch (e: Exception) {
                try {
                    snapshot.child(key).value<Long>().toDouble()
                } catch (e2: Exception) {
                    try {
                        snapshot.child(key).value<Int>().toDouble()
                    } catch (e3: Exception) {
                        try {
                            snapshot.child(key).value<String>().toDouble()
                        } catch (e4: Exception) {
                            0.0
                        }
                    }
                }
            }
        }
        
        fun safeReadInt(key: String): Int {
            if (!snapshot.child(key).exists) return 0
            return try {
                snapshot.child(key).value<Int>()
            } catch (e: Exception) {
                try {
                    snapshot.child(key).value<Long>().toInt()
                } catch (e2: Exception) {
                    try {
                        snapshot.child(key).value<Double>().toInt()
                    } catch (e3: Exception) {
                        try {
                            snapshot.child(key).value<String>().toInt()
                        } catch (e4: Exception) {
                            0
                        }
                    }
                }
            }
        }
        
        return UserPresence(
            userId = safeRead("userId") { snapshot.child("userId").value<String>() } ?: "",
            email = safeRead("email") { snapshot.child("email").value<String>() } ?: "",
            deviceId = safeRead("deviceId") { snapshot.child("deviceId").value<String>() } ?: snapshot.key ?: "",
            username = safeRead("username") { snapshot.child("username").value<String>() } ?: "",
            color = safeReadInt("color"),
            cursorPosition = safeReadInt("cursorPosition"),
            activeSectionId = safeRead("activeSectionId") { snapshot.child("activeSectionId").value<String>() },
            activeLineId = safeRead("activeLineId") { snapshot.child("activeLineId").value<String>() },
            activeTrackId = safeRead("activeTrackId") { snapshot.child("activeTrackId").value<String>() },
            activeTrackItemId = safeRead("activeTrackItemId") { snapshot.child("activeTrackItemId").value<String>() },
            selectedTrackItemIds = safeRead("selectedTrackItemIds") { snapshot.child("selectedTrackItemIds").value<String>() }?.split(",")?.filter { it.isNotBlank() }?.toSet() ?: emptySet(),
            activeTabId = safeRead("activeTabId") { snapshot.child("activeTabId").value<String>() },
            tabCursorColumn = safeReadInt("tabCursorColumn"),
            tabCursorString = safeReadInt("tabCursorString"),
            lastActive = safeReadDouble("lastActive")
        )
    }

    suspend fun updatePresence(songId: String, presence: UserPresence) {
        try {
            // Get the current actual authenticated UID to ensure security rules pass
            val auth = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
            val uid = auth?.uid ?: return // STRICT: Only write to our own node
            
            val key = presence.deviceId.ifBlank { deviceId }
            val userRef = database.reference("songs/$songId/presence/$uid")
            val deviceRef = userRef.child(key)
            
            try {
                deviceRef.onDisconnect().removeValue()
            } catch (e: Exception) {}

            val nowStr = Clock.System.now().toEpochMilliseconds().toString()
            val now = nowStr.toDouble()
            val presenceMap = mapOf(
                "userId" to uid,
                "email" to presence.email,
                "username" to presence.username,
                "color" to presence.color,
                "deviceId" to key,
                "activeSectionId" to presence.activeSectionId,
                "activeLineId" to presence.activeLineId,
                "activeTrackId" to presence.activeTrackId,
                "activeTrackItemId" to presence.activeTrackItemId,
                "selectedTrackItemIds" to presence.selectedTrackItemIds.joinToString(","),
                "activeTabId" to presence.activeTabId,
                "tabCursorColumn" to presence.tabCursorColumn,
                "tabCursorString" to presence.tabCursorString,
                "lastActive" to now, // FORCE the primitive JS number
                "cursorPosition" to presence.cursorPosition
            )
            
            // ELITE FIX: Satisfy the ".validate": "newData.hasChildren(['lastActive'])" rule
            // at the /presence/$userId level by writing lastActive to the parent node as well.
            userRef.updateChildren(mapOf(
                "lastActive" to now,
                key to presenceMap
            ))
        } catch (e: Exception) {
            println("DEBUG_REPO: Error updating presence: $e")
        }
    }

    fun pushUpdate(
        song: Song, 
        songId: String, 
        userId: String, 
        scope: UndoScope, 
        shouldPushToCloud: Boolean = true, 
        forceUndoStep: Boolean = false,
        isUndoable: Boolean = true
    ) {
        this.scope.launch {
            pushUpdateSuspend(song, songId, userId, scope, shouldPushToCloud, forceUndoStep, isUndoable)
        }
    }

    suspend fun pushUpdateSuspend(
        song: Song, 
        songId: String, 
        userId: String, 
        scope: UndoScope, 
        shouldPushToCloud: Boolean = true, 
        forceUndoStep: Boolean = false,
        isUndoable: Boolean = true
    ) {
        crdtMutex.withLock {
            if (currentSongId != songId) return@withLock

            val songBeforeUpdate = crdtDoc.getSong()
            
            // TRACKING: Identify local structural additions
            when (scope) {
                UndoScope.EDITOR -> {
                    val existingSectionIds = songBeforeUpdate.sections.map { it.id }.toSet()
                    song.sections.forEach { if (!existingSectionIds.contains(it.id)) locallyAddedSectionIds.add(it.id) }
                }
                UndoScope.STUDIO -> {
                    val existingTrackIds = songBeforeUpdate.studioState?.tracks?.map { it.id }?.toSet() ?: emptySet()
                    song.studioState?.tracks?.forEach { track ->
                        if (!existingTrackIds.contains(track.id)) locallyAddedTrackIds.add(track.id)
                        val existingItemIds = songBeforeUpdate.studioState?.tracks?.find { it.id == track.id }?.items?.map { it.id }?.toSet() ?: emptySet()
                        track.items.forEach { if (!existingItemIds.contains(it.id)) locallyAddedTrackItemIds.add(it.id) }
                    }
                }
            }

            // PRO UNDO: Store snapshot BEFORE update
            if (isUndoable) {
                pushToUndoStack(songBeforeUpdate, song, scope = scope, force = forceUndoStep)
            }

            crdtDoc.update(song)
            val delta = crdtDoc.getChangesSince(lastState)
            
            if (delta.isNotEmpty()) {
                requestUiUpdate(immediate = true)

                if (shouldPushToCloud) {
                    val deltaBase64 = Utils.byteArrayToBase64(delta)
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val key = "${timestamp}_$userId"
                    
                    lastState = crdtDoc.save()
                    appliedDeltaKeys.add(key)

                    _pendingDeltasCount.value++
                    localPushCount++
                    
                    val shouldCleanup = localPushCount % 50 == 0
                    
                    this@SongCrdtSyncManager.scope.launch {
                        try {
                            val deltasRef = database.reference("songs/$songId/deltas")
                            deltasRef.child(key).setValue(deltaBase64)
                            
                            if (shouldCleanup) {
                                try {
                                    deltasRef.removeValue()
                                    deltasRef.child(key).setValue(deltaBase64)
                                } catch (e: Exception) {}
                            }
                        } catch (e: Exception) {
                        } finally {
                            _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                        }
                    }
                }
            }
        }
    }

    fun clearPresence(songId: String) {
        scope.launch {
            try {
                val auth = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                val uid = auth?.uid ?: return@launch
                database.reference("songs/$songId/presence/$uid/$deviceId").removeValue()
                database.reference("songs/$songId/presence/$uid/$sessionDeviceId").removeValue()
            } catch (e: Exception) {
                println("DEBUG_CRDT: Error clearing presence: $e")
            }
        }
    }

    fun clearRemotePresence(songId: String, userId: String, deviceId: String) {
        scope.launch {
            try {
                database.reference("songs/$songId/presence/$userId/$deviceId").removeValue()
            } catch (e: Exception) {}
        }
    }

    /**
     * Wipes ALL device presence nodes for a specific user (e.g. when removed from song).
     */
    fun clearAllPresenceForUser(songId: String, userId: String) {
        scope.launch {
            try {
                database.reference("songs/$songId/presence/$userId").removeValue()
            } catch (e: Exception) {}
        }
    }

    fun clearDeltas(songId: String, keys: Set<String>) {
        if (keys.isEmpty()) return
        scope.launch {
            val deltasRef = database.reference("songs/$songId/deltas")
            keys.forEach { key ->
                try {
                    deltasRef.child(key).removeValue()
                } catch (e: Exception) {}
            }
        }
    }

    fun clearAllDeltas(songId: String) {
        scope.launch {
            try {
                database.reference("songs/$songId/deltas").removeValue()
            } catch (e: Exception) {}
        }
    }

    suspend fun getCurrentSnapshotWithKeys(): Pair<ByteArray?, Set<String>> {
        return crdtMutex.withLock {
            val state = crdtDoc.save()
            val keys = appliedDeltaKeys.toSet()
            state to keys
        }
    }

    suspend fun getCurrentState(): ByteArray? {
        return crdtMutex.withLock {
            crdtDoc.save()
        }
    }

    suspend fun reinitialize(song: Song) {
        crdtMutex.withLock {
            crdtDoc = SongCrdtDoc()
            crdtDoc.update(song)
            lastState = crdtDoc.save()
            requestUiUpdate(immediate = true)
        }
    }
    fun stopSync(songId: String? = null, userId: String? = null) {
        if (songId != null && currentSongId != null && currentSongId != songId) return

        if (songId != null) {
            scope.launch {
                try {
                    val auth = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                    val uid = auth?.uid ?: "anonymous"
                    
                    // Clear both persistent and session entries to be safe
                    database.reference("songs/$songId/presence/$uid/$deviceId").removeValue()
                    database.reference("songs/$songId/presence/$uid/$sessionDeviceId").removeValue()
                } catch (e: Exception) {}
            }
        }
        syncJob?.cancel()
        syncJob = null
        currentSongId = null
        lastLoadedSnapshot = null
        crdtDoc = SongCrdtDoc()
        lastState = null
        appliedDeltaKeys.clear()
        
        editorUndoStack.clear()
        editorRedoStack.clear()
        studioUndoStack.clear()
        studioRedoStack.clear()
        updateUndoRedoStatus()
    }

    private fun getReconstructedSong(): Song {
        val song = crdtDoc.getSong()
        return currentSongId?.let { 
            val finalOwnerId = currentOwnerId.ifBlank { song.ownerId }
            song.copy(
                id = it,
                ownerId = finalOwnerId,
                ownerUsername = currentOwnerId.takeIf { it == currentOwnerId && it.isNotEmpty() }?.let { currentOwnerId } ?: song.ownerUsername,
                collaborators = currentCollaborators,
                viewers = currentViewers,
                isPublic = if (finalOwnerId.isNotEmpty()) (song.isPublic) else false,
                isDeleted = if (finalOwnerId.isNotEmpty()) (song.isDeleted) else false
            )
        } ?: song
    }
    
    fun getCurrentSong(): Song? {
        return _songFlow.value
    }

    // --- PRO COLLABORATION: Atomic Text Splicing ---

    fun pushTextUpdate(
        songId: String,
        userId: String,
        sectionId: String,
        lineId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        shouldPushToCloud: Boolean = true
    ) {
        scope.launch {
            crdtMutex.withLock {
                if (currentSongId != songId) return@withLock
                
                val before = crdtDoc.getSong()
                crdtDoc.spliceLineText(sectionId, lineId, index, deleteCount, insertText)
                val after = crdtDoc.getSong()
                val delta = crdtDoc.getChangesSince(lastState)
                
                if (delta.isNotEmpty()) {
                    pushToUndoStack(before, after, scope = UndoScope.EDITOR, force = false)
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val key = "${timestamp}_$userId"
                    lastState = crdtDoc.save()
                    appliedDeltaKeys.add(key)
                    requestUiUpdate(immediate = true)

                    if (shouldPushToCloud) {
                        val deltaBase64 = Utils.byteArrayToBase64(delta)
                        _pendingDeltasCount.value++
                        this@SongCrdtSyncManager.scope.launch {
                            try {
                                database.reference("songs/$songId/deltas/$key").setValue(deltaBase64)
                            } catch (e: Exception) {
                                println("DEBUG_CRDT: Error pushing delta: $e")
                            } finally {
                                _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                            }
                        }
                    }
                }
            }
        }
    }

    fun pushSectionTitleUpdate(
        songId: String,
        userId: String,
        sectionId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        shouldPushToCloud: Boolean = true
    ) {
        scope.launch {
            crdtMutex.withLock {
                if (currentSongId != songId) return@withLock
                
                val before = crdtDoc.getSong()
                crdtDoc.spliceSectionTitle(sectionId, index, deleteCount, insertText)
                val after = crdtDoc.getSong()
                val delta = crdtDoc.getChangesSince(lastState)
                
                if (delta.isNotEmpty()) {
                    pushToUndoStack(before, after, scope = UndoScope.EDITOR, force = false)
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val key = "${timestamp}_$userId"
                    lastState = crdtDoc.save()
                    appliedDeltaKeys.add(key)
                    requestUiUpdate(immediate = true)

                    if (shouldPushToCloud) {
                        val deltaBase64 = Utils.byteArrayToBase64(delta)
                        _pendingDeltasCount.value++
                        this@SongCrdtSyncManager.scope.launch {
                            try {
                                database.reference("songs/$songId/deltas/$key").setValue(deltaBase64)
                            } catch (e: Exception) {
                                println("DEBUG_CRDT: Error pushing delta: $e")
                            } finally {
                                _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                            }
                        }
                    }
                }
            }
        }
    }

    fun pushSongTitleUpdate(
        songId: String,
        userId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        shouldPushToCloud: Boolean = true
    ) {
        scope.launch {
            crdtMutex.withLock {
                if (currentSongId != songId) return@withLock
                
                val before = crdtDoc.getSong()
                crdtDoc.spliceSongTitle(index, deleteCount, insertText)
                val after = crdtDoc.getSong()
                val delta = crdtDoc.getChangesSince(lastState)
                
                if (delta.isNotEmpty()) {
                    pushToUndoStack(before, after, scope = UndoScope.EDITOR, force = false)
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val key = "${timestamp}_$userId"
                    lastState = crdtDoc.save()
                    appliedDeltaKeys.add(key)
                    requestUiUpdate(immediate = true)

                    if (shouldPushToCloud) {
                        val deltaBase64 = Utils.byteArrayToBase64(delta)
                        _pendingDeltasCount.value++
                        this@SongCrdtSyncManager.scope.launch {
                            try {
                                database.reference("songs/$songId/deltas/$key").setValue(deltaBase64)
                            } catch (e: Exception) {
                                println("DEBUG_CRDT: Error pushing delta: $e")
                            } finally {
                                _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                            }
                        }
                    }
                }
            }
        }
    }

    fun pushSongNotesUpdate(
        songId: String,
        userId: String,
        index: Int,
        deleteCount: Int,
        insertText: String,
        shouldPushToCloud: Boolean = true
    ) {
        scope.launch {
            crdtMutex.withLock {
                if (currentSongId != songId) return@withLock
                
                val before = crdtDoc.getSong()
                crdtDoc.spliceSongNotes(index, deleteCount, insertText)
                val after = crdtDoc.getSong()
                val delta = crdtDoc.getChangesSince(lastState)
                
                if (delta.isNotEmpty()) {
                    pushToUndoStack(before, after, scope = UndoScope.EDITOR, force = false)
                    val timestamp = Clock.System.now().toEpochMilliseconds()
                    val key = "${timestamp}_$userId"
                    lastState = crdtDoc.save()
                    appliedDeltaKeys.add(key)
                    requestUiUpdate(immediate = true)

                    if (shouldPushToCloud) {
                        val deltaBase64 = Utils.byteArrayToBase64(delta)
                        _pendingDeltasCount.value++
                        this@SongCrdtSyncManager.scope.launch {
                            try {
                                database.reference("songs/$songId/deltas/$key").setValue(deltaBase64)
                            } catch (e: Exception) {
                                println("DEBUG_CRDT: Error pushing delta: $e")
                            } finally {
                                _pendingDeltasCount.value = (_pendingDeltasCount.value - 1).coerceAtLeast(0)
                            }
                        }
                    }
                }
            }
        }
    }
}
